import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { getAuthToken } from '../utils/storage';
import { extractApiError } from '../utils/apiError';

/**
 * Axios instance for product catalogue-related API calls
 * - Base URL `${API_BASE_URL}/api/v1`
 */
import { applyWarehouseFilter, warehouseParamsSerializer } from '../utils/warehouseParams';

/**
 * Axios instance for product catalogue-related API calls
 * - Base URL `${API_BASE_URL}/api/v1`
 */
// Create axios instance for product catalogue API calls
const productCatalogueAPI = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: warehouseParamsSerializer,
});

/**
 * Request interceptor
 * - Attaches stored auth token when present. Includes a short debug log
 *   which can help during development. Remove or gate this log in prod.
 */
// Add request interceptor to include auth token
productCatalogueAPI.interceptors.request.use(
  (config) => {
    const token = getAuthToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      // Debug log to verify token is being sent (remove in production)
      console.log('Sending Bearer token with productCatalogueAPI request:', token.substring(0, 20) + '...');
    } else {
      console.warn('No auth token found in storage for productCatalogueAPI request');
    }
    return applyWarehouseFilter(config);
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor
 * - Clears local/session storage and redirects to login on 401 responses
 */
// Add response interceptor to handle auth errors
productCatalogueAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear storage and redirect to login
      localStorage.removeItem('auth_token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_data');
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('user_data');

      // Redirect to login page
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * Normalize axios error objects into human-readable messages.
 * - Tries common server response shapes and falls back to generic text
 */
// Helper to extract backend error details from axios error

/**
 * Product Catalogue Service Class
 * Handles all API operations related to product catalogue
 */
class ProductCatalogueService {
  /**
   * Get all product catalogue items with optional pagination and filtering
   * @param {Object} params - Query parameters (page, limit, search, filter)
   * @returns {Promise<Object>} Product catalogue list with pagination info
   */
  async getProductCatalogue(params = {}) {
    try {
      const queryParams = new URLSearchParams();

      // Add pagination parameters - API expects 'skip' instead of 'page'
      if (params.page !== undefined) {
        const skip = (params.page - 1) * (params.limit || 20);
        queryParams.append('skip', skip);
      } else if (params.skip !== undefined) {
        queryParams.append('skip', params.skip);
      } else {
        queryParams.append('skip', 0); // Default skip value
      }

      if (params.limit) queryParams.append('limit', params.limit);
      else queryParams.append('limit', 20); // Default limit value

      if (params.search !== undefined) queryParams.append('search', params.search);
      if (params.category_id) queryParams.append('category_id', params.category_id);
      // min_price/max_price removed — price moved to the SKU (migration 061); the
      // catalogue list no longer filters by price.

      const response = await productCatalogueAPI.get(`/product-catalogue?${queryParams.toString()}`);

      return {
        success: true,
        data: response.data.items || response.data.products || response.data,
        pagination: response.data.pagination || {},
        total: response.data.total ?? response.data.count ?? response.data.length,
      };
    } catch (error) {
      console.error('Get product catalogue error:', error.response?.data || error.message || error);
      return {
        success: false,
        error: extractApiError(error),
        data: [],
      };
    }
  }

  /**
   * Get product catalogue item by ID
   * @param {number} id - Product catalogue item ID
   * @returns {Promise<Object>} Product catalogue item details
   */
  async getProductCatalogueById(id) {
    try {
      const response = await productCatalogueAPI.get(`/product-catalogue/${id}`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error(`Get product catalogue ${id} error:`, error.response?.data || error.message || error);
      return {
        success: false,
        error: extractApiError(error),
        data: null,
      };
    }
  }

  /**
   * Create a new product catalogue item
   * @param {Object} productData - Product catalogue item data
   * @returns {Promise<Object>} Created product catalogue item
   */
  async createProductCatalogue(productData) {
    try {
      const response = await productCatalogueAPI.post('/product-catalogue', productData);
      return {
        success: true,
        data: response.data,
        message: 'Product catalogue item created successfully',
      };
    } catch (error) {
      console.error('Create product catalogue error:', error.response?.data || error.message || error);
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  }

  /**
   * Update an existing product catalogue item
   * @param {number} id - Product catalogue item ID
   * @param {Object} productData - Updated product catalogue item data
   * @returns {Promise<Object>} Updated product catalogue item
   */
  async updateProductCatalogue(id, productData) {
    try {
      const response = await productCatalogueAPI.put(`/product-catalogue/${id}`, productData);
      return {
        success: true,
        data: response.data,
        message: 'Product catalogue item updated successfully',
      };
    } catch (error) {
      console.error(`Update product catalogue ${id} error:`, error.response?.data || error.message || error);
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  }

  /**
   * Delete a product catalogue item
   * @param {number} id - Product catalogue item ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteProductCatalogue(id) {
    try {
      const response = await productCatalogueAPI.delete(`/product-catalogue/${id}`);
      return {
        success: true,
        data: response.data,
        message: 'Product catalogue item deleted successfully',
      };
    } catch (error) {
      console.error(`Delete product catalogue ${id} error:`, error.response?.data || error.message || error);
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  }

  /**
   * Bulk delete multiple product catalogue items
   * @param {Array<number>} ids - Array of product catalogue item IDs
   * @returns {Promise<Object>} Bulk deletion result
   */
  async bulkDeleteProductCatalogue(ids) {
    try {
      // API expects a top-level JSON array of ids in the request body, e.g. [2,3,4]
      const response = await productCatalogueAPI.post('/product-catalogue/bulk-delete', ids);
      return {
        success: true,
        data: response.data,
        message: `${ids.length} product catalogue items deleted successfully`,
      };
    } catch (error) {
      console.error('Bulk delete product catalogue error:', error.response?.data || error.message || error);
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  }

  /**
   * Import product catalogue items from Excel file
   * @param {File} file - Excel file containing product catalogue data
   * @returns {Promise<Object>} Import result
   */
  async importProductCatalogue(file) {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await productCatalogueAPI.post('product-catalogue/import/excel', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return {
        success: true,
        data: response.data,
        message: 'Product catalogue imported successfully',
      };
    } catch (error) {
      console.error('Import product catalogue error:', error.response?.data || error.message || error);
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  }

  /**
   * Export product catalogue to Excel
   * @param {Object} params - Export parameters (filters, etc.)
   * @returns {Promise<Blob>} Excel file blob
   */
  async exportProductCatalogue(params = {}) {
    try {
      const queryParams = new URLSearchParams();

      if (params.search !== undefined) queryParams.append('search', params.search);
      if (params.category_id) queryParams.append('category_id', params.category_id);
      // min_price/max_price removed — price moved to the SKU (migration 061).

      const query = queryParams.toString();
      const url = `/product-catalogue/export/download${query ? `?${query}` : ''}`;
      const response = await productCatalogueAPI.get(url, {
        responseType: 'blob',
      });

      return {
        success: true,
        data: response.data,
        headers: response.headers,
      };
    } catch (error) {
      console.error('Export product catalogue error:', error.response?.data || error.message || error);
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  }

  /**
   * Download sample Excel template for product catalogue import
   * @returns {Promise<Blob>} Sample Excel file blob
   */
  async downloadSampleTemplate() {
    try {
      const response = await productCatalogueAPI.get('/product-catalogue/sample-template', {
        responseType: 'blob',
      });

      return {
        success: true,
        data: response.data,
        filename: response.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') || 'product_catalogue_sample.xlsx',
      };
    } catch (error) {
      console.error('Download sample template error:', error.response?.data || error.message || error);
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  }

  /**
   * Get product categories for dropdown selection
   * @returns {Promise<Object>} Product categories list
   */
  async getProductCategories() {
    try {
      const response = await productCatalogueAPI.get('/product-categories');
      return {
        success: true,
        data: response.data.categories || response.data,
      };
    } catch (error) {
      console.error('Get product categories error:', error.response?.data || error.message || error);
      return {
        success: false,
        error: extractApiError(error),
        data: [],
      };
    }
  }

  /**
   * Get product category by ID
   * @param {number} id - Product category ID
   * @returns {Promise<Object>} Product category details
   */
  async getProductCategoryById(id) {
    try {
      const response = await productCatalogueAPI.get(`/product-categories/${id}`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error(`Get product category ${id} error:`, error.response?.data || error.message || error);
      return {
        success: false,
        error: extractApiError(error),
        data: null,
      };
    }
  }

  /**
   * Get product category statistics
   * @returns {Promise<Object>} Product category statistics
   */
  async getProductCategoryStats() {
    try {
      const response = await productCatalogueAPI.get('/product-categories/stats');
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Get product category stats error:', error.response?.data || error.message || error);
      return {
        success: false,
        error: extractApiError(error),
        data: {},
      };
    }
  }

  /**
   * Get all products for dropdown selection (without pagination)
   * @returns {Promise<Object>} All products list
   */
  async getAllProducts() {
    // Calling the endpoint with no params applies the backend's default page
    // size (20), which silently truncates dropdowns. The backend caps `limit` at
    // 100 (higher values 500-error), so page through at the max page size until
    // we've collected every item — correct regardless of how many exist.
    const PAGE_LIMIT = 100;
    try {
      const all = [];
      let skip = 0;
      let total = Infinity;

      // Safety bound: never loop more than a sane number of pages.
      for (let page = 0; page < 100 && skip < total; page += 1) {
        const response = await productCatalogueAPI.get('/product-catalogue', {
          params: { skip, limit: PAGE_LIMIT },
        });
        const body = response.data || {};
        const items = body.items || body.products || (Array.isArray(body) ? body : []);

        all.push(...items);

        // Determine the true total to know when to stop.
        total = typeof body.total === 'number' ? body.total : all.length;
        skip += PAGE_LIMIT;

        // No total field and a short page → we've reached the end.
        if (items.length < PAGE_LIMIT) break;
      }

      return { success: true, data: all };
    } catch (error) {
      console.error('Get all products error:', error.response?.data || error.message || error);
      return {
        success: false,
        error: extractApiError(error),
        data: [],
      };
    }
  }

  /**
   * Get HSN codes for dropdown selection
   * @returns {Promise<Object>} HSN codes list
   */
  async getHsnCodes() {
    try {
      const response = await productCatalogueAPI.get('/hsn-codes');
      return {
        success: true,
        data: response.data.hsn_codes || response.data,
      };
    } catch (error) {
      console.error('Get HSN codes error:', error.response?.data || error.message || error);
      return {
        success: false,
        error: extractApiError(error),
        data: [],
      };
    }
  }
}

// Export a singleton instance
const productCatalogueService = new ProductCatalogueService();
export default productCatalogueService;